/**
 * El enlace con el que un cliente invita a su gestoría — `OD-0063` D-04,
 * enmienda del 2026-10-28.
 *
 * ## Por qué existe
 *
 * El cliente pulsa «Invitar a mi asesoría» y se abre SU correo con un enlace
 * `/registro?invitacion-cliente=<token>` (`mailto`). La gestoría lo abre, y
 * entre ese momento y tener una empresa en Faktelio hay un alta con un correo
 * de confirmación que se abre en OTRA pestaña, como con el código de
 * `asesoria-del-enlace.ts`. Sin recordarlo, el token se perdería antes de
 * usarse. Si la gestoría ya tenía cuenta, entra, y el token la espera igual.
 *
 * ## Lo que esto NO es
 *
 * **No da acceso a nada.** El Inicio manda el token a la API, que apunta qué
 * organización entró por él; el acceso lo da después el cliente, con un clic.
 *
 * **No valida el token.** Solo su forma —256 bits en base64url—, la misma que
 * mira la API (`formaDeTokenDeInvitacion`). Cuál existe lo sabe la API.
 *
 * **Es un secreto pequeño:** quien tenga el token puede poner su nombre en la
 * pantalla del cliente. Por eso se borra de la barra al cargar (`SE_LIMPIA`),
 * se olvida en cuanto la API contesta, y caduca como el enlace: 30 días.
 */

/** El parámetro del enlace: `/registro?invitacion-cliente=<token>`. */
export const PARAMETRO = "invitacion-cliente";

const CLAVE = "faktelio.invitacion-de-cliente";

/** Lo mismo que vale el enlace en la API (`DIAS_DE_LA_INVITACION_DE_CLIENTE`). */
export const CADUCA_MS = 30 * 24 * 60 * 60 * 1000;

export function formaDeToken(valor: unknown): string | null {
  return typeof valor === "string" && /^[A-Za-z0-9_-]{43}$/.test(valor) ? valor : null;
}

/** El token que trae la URL, o `null`. */
export function tokenDelEnlace(search: string): string | null {
  return formaDeToken(new URLSearchParams(search).get(PARAMETRO));
}

/** El enlace que el cliente manda: a la aplicación, que es quien lo lee. */
export function enlaceDeInvitacion(origen: string, camino: string, token: string): string {
  return `${origen}${camino}?${PARAMETRO}=${encodeURIComponent(token)}`;
}

/**
 * Un `mailto:` sin destinatario: la dirección la escribe el cliente en su
 * correo, y Faktelio no la ve. Los saltos de línea van como CRLF, que es lo
 * que pide RFC 6068 para el cuerpo.
 */
export function mailtoDeInvitacion(asunto: string, cuerpo: string): string {
  const q = (v: string) => encodeURIComponent(v.replace(/\r?\n/g, "\r\n"));
  return `mailto:?subject=${q(asunto)}&body=${q(cuerpo)}`;
}

function almacen(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function recordarInvitacion(token: string, ahora: number): void {
  try {
    almacen()?.setItem(CLAVE, JSON.stringify({ token, en: ahora }));
  } catch {
    // Sin almacenamiento se sigue: el Inicio de esta pestaña lo lee de aquí si puede.
  }
}

/** El token de una visita anterior, si sigue vigente. */
export function invitacionPendiente(ahora: number): string | null {
  let crudo: string | null = null;
  try {
    crudo = almacen()?.getItem(CLAVE) ?? null;
  } catch {
    return null;
  }
  if (crudo === null) return null;
  try {
    const v = JSON.parse(crudo) as { token?: unknown; en?: unknown };
    const token = formaDeToken(v.token);
    const edad = typeof v.en === "number" ? ahora - v.en : -1;
    if (token !== null && edad >= 0 && edad < CADUCA_MS) return token;
  } catch {
    // Ilegible: como si no hubiera nada, y se borra.
  }
  olvidarInvitacion();
  return null;
}

/** Se olvida en cuanto la API contesta, sea lo que sea. */
export function olvidarInvitacion(): void {
  try {
    almacen()?.removeItem(CLAVE);
  } catch {
    // Nada que hacer: caduca solo.
  }
}

const DESCARTADAS = "faktelio.asesorias-descartadas";

/**
 * «Ahora no» en la tarjeta del Inicio del CLIENTE: esa gestoría deja de
 * ofrecerse en ESTE navegador. Es una comodidad, no una decisión: en
 * Configuración > Asesoría sigue saliendo, y sin almacenamiento vuelve a salir.
 */
export function descartarOferta(invitacion: string): void {
  try {
    const previas = leerDescartadas().filter((x) => x !== invitacion);
    almacen()?.setItem(DESCARTADAS, JSON.stringify([invitacion, ...previas].slice(0, 20)));
  } catch {
    // Sin almacenamiento vuelve a salir la proxima vez.
  }
}

export function ofertaDescartada(invitacion: string): boolean {
  return leerDescartadas().includes(invitacion);
}

function leerDescartadas(): string[] {
  try {
    const v: unknown = JSON.parse(almacen()?.getItem(DESCARTADAS) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
